import * as Sentry from '@sentry/nextjs';
import { completeRegistration } from '@util/analytics';
import { db, functions, httpsCallable } from '@util/firebase';
import { logError } from '@util/logError';
import { AttributionData } from '@util/marketing';
import { AddressDocument } from '@util/types/firestore/address';
import {
  UserDocument,
  userDocumentSchema,
  spotlightInfoSchema,
} from '@util/types/firestore/users';
import { slugify } from '@util/urlHelpers';
import AuthProvider from 'context/AuthContext';
import { FirebaseError } from 'firebase/app';
import {
  arrayUnion,
  arrayRemove,
  collection,
  CollectionReference,
  doc,
  getDoc,
  getDocFromServer,
  getDocs,
  deleteDoc,
  limit,
  query,
  setDoc,
  updateDoc,
  where,
  deleteField,
  onSnapshot,
} from 'firebase/firestore';
import { ShipEngineValidateResponse } from '../shipengine';
import {
  CreateUserDocumentArgs,
  GetUserArgs,
  UpdateUserDocArgs,
  updateUserDocArgsSchema,
  UpdateUserRoleArgs,
  usernameSchema,
} from './user.types';

export const userRef = collection(
  db,
  'users'
) as CollectionReference<UserDocument>;

const userCol = collection(db, 'users') as CollectionReference<UserDocument>;
const getRef = (uid: string) => doc<UserDocument>(userCol, uid);

export async function getUserDocument(
  args: GetUserArgs
): Promise<UserDocument | null> {
  if (!args.uid) return null;
  const docSnap = await getDoc(doc(db, 'users', args.uid));
  const user = (docSnap.data() as UserDocument) ?? null;
  return user;
}

// GB #1526: stable Sentry fingerprints for the D1 cold-cache meter. Explicit
// fingerprints keep the two outcomes in SEPARATE Sentry issues, so each issue's
// event count is directly readable as its own number.
const COLD_CACHE_FINGERPRINT = 'auth-cold-cache-empty-suppressed';

/**
 * GB #1526: how long the suppressed state may persist before the user counts as
 * STUCK rather than merely delayed. `resolveAuthLandingAction` returns 'wait'
 * for a signed-in user whose doc is still loading, so a suppression that never
 * resolves is a user sitting on a spinner — not a user the D1 fix saved. 10s is
 * far longer than a healthy server snapshot (sub-second) and short enough that
 * the listener is usually still mounted to report it.
 */
export const COLD_CACHE_RESOLVE_TIMEOUT_MS = 10_000;

/**
 * GB #1526: a timer that fires this much later than its delay was throttled or
 * suspended by the browser (background tabs are throttled to ~1 wake/min, and
 * iOS freezes them outright) — the user was not watching a spinner for that
 * whole time. Reporting those as stuck users would be fabricating the
 * measurement this issue exists to produce.
 */
const COLD_CACHE_LATE_TIMER_TOLERANCE_MS = 5_000;

type ColdCacheOutcome =
  /** A SERVER doc arrived after a suppression: a user the D1 fix saved. */
  | 'cold_cache_suppressed_recovered'
  /** Nothing ever arrived: a user stuck on the spinner. */
  | 'cold_cache_suppressed_unresolved'
  /** The listener died on a transport error mid-suppression. */
  | 'cold_cache_suppressed_transport_error';

/**
 * GB #1526: Sentry's ingest quota is an ORG-WIDE shared resource — a 429 drops
 * events for checkout and payments too, and the 2026-07-01 → 07-17 blackout was
 * exactly that. Every outcome is therefore capped at ONE event per kind per
 * full page load.
 *
 * Precisely "per page load", NOT "per tab session": module scope survives
 * Next.js soft navigations but resets on every hard document load — including
 * AuthContext's own `window.location.reload()` and `assign('/')` on logout.
 *
 * Why all three kinds are capped, not just the common one: the trigger is an
 * unreachable Firestore, which is a SITE-WIDE condition. Every authenticated
 * tab would emit simultaneously, into the shared quota, at exactly the moment
 * checkout errors matter most (adversarial + zero-context review both caught
 * this). `sentryFloodGuard` does not help here — it is per-browser and its
 * window is 10s, so hourly emissions each open a fresh window.
 */
const coldCacheCaptureUsed: Record<ColdCacheOutcome, boolean> = {
  cold_cache_suppressed_recovered: false,
  cold_cache_suppressed_unresolved: false,
  cold_cache_suppressed_transport_error: false,
};

/**
 * GB #1526: a second, PERSISTENT throttle on top of the per-page-load cap.
 *
 * The in-memory cap resets on every hard document load, so during a prolonged
 * Firestore outage one user reloading a broken page repeatedly would emit once
 * per reload (Codex P1). This bounds any single browser to one event per kind
 * per hour, which is ample for a meter whose question is "does this fire, and
 * roughly how often".
 *
 * What it deliberately does NOT do: bound emissions ACROSS clients. During a
 * site-wide Firestore outage every authenticated browser still contributes its
 * first event. No client-side mechanism can fix that — only sampling can, and
 * sampling would blind the meter to a RARE condition, which is precisely the
 * question #1435 needs answered ("if it never fires while double-logins
 * continue, the premise is wrong"). That trade-off is Bill's call, not one to
 * make silently: see the PR body.
 */
const COLD_CACHE_THROTTLE_KEY = 'gf_cc_meter';
const COLD_CACHE_THROTTLE_MS = 60 * 60 * 1000;

/** Fails OPEN: a storage error must never silence the meter. */
function coldCacheThrottleAllows(
  kind: ColdCacheOutcome,
  now: number
): boolean {
  try {
    const raw = window.localStorage.getItem(
      `${COLD_CACHE_THROTTLE_KEY}:${kind}`
    );
    // An ABSENT record must be treated as "never sent". Reading it as a number
    // without this check is a trap: `Number(null)` is 0, which reads as "sent
    // at the epoch" and blocks every event until the clock is an hour past
    // 1970 — harmless with a real clock, but wrong, and it silently broke the
    // tests that pin this behavior with a mocked clock.
    if (raw === null || raw === '') return true;
    const last = Number(raw);
    if (!Number.isFinite(last)) return true; // tampered / garbage → allow
    // `last > now` means the clock moved backwards — treat as stale and allow,
    // rather than blocking until it catches up, which could be indefinite.
    if (last <= now && now - last < COLD_CACHE_THROTTLE_MS) return false;
    return true;
  } catch {
    return true;
  }
}

/**
 * Records a SENT event. Split from the check so a throwing Sentry transport
 * cannot consume the browser's hourly allowance while delivering nothing —
 * the same reasoning as the in-memory cap being marked only on success.
 */
function coldCacheThrottleCommit(kind: ColdCacheOutcome, now: number): void {
  try {
    window.localStorage.setItem(
      `${COLD_CACHE_THROTTLE_KEY}:${kind}`,
      String(now)
    );
  } catch {
    // Storage unavailable — the in-memory cap still bounds this page.
  }
}

/**
 * Test-only reset for the module-scope caps above. Kept as an export (it is
 * re-exported by this folder's barrel) in preference to rebuilding the test
 * file around `jest.resetModules`, which would re-create the hoisted
 * `firebase/firestore` mock and break the #1435 D1 regression tests that guard
 * the routing behavior. No production caller; reviewed and accepted as LOW.
 */
export function __resetColdCacheCapsForTests(): void {
  coldCacheCaptureUsed.cold_cache_suppressed_recovered = false;
  coldCacheCaptureUsed.cold_cache_suppressed_unresolved = false;
  coldCacheCaptureUsed.cold_cache_suppressed_transport_error = false;
}

export function listenToUserDocument(
  uid: string,
  callback: (doc: UserDocument | null) => void,
  // GB #1534: fired after the existing error handling below, so a caller
  // (AuthContext) can re-subscribe a listener Firestore has killed on a
  // transport error. Optional and additive — no existing caller passes it,
  // so this changes no behavior on its own.
  onTransportError?: (err: unknown) => void,
  // GB #1534 (FRESH EYES): fires ONLY for a snapshot confirmed to have come
  // from the SERVER (snapshot.metadata.fromCache === false), whether the doc
  // exists or is server-confirmed absent. A resubscribe on a warm cache can
  // deliver a cached exists()===true snapshot via `callback` before the
  // watch stream has even reconnected — the same mechanism the D1 fix above
  // documents for a cold, empty cache. That cache-sourced delivery is NOT
  // proof the connection recovered, so a caller using `callback` alone to
  // decide "the connection is healthy again" (e.g. to reset a retry
  // counter) can be defeated by a persistently-broken watch stream that
  // keeps re-serving the same stale cached doc on every resubscribe. This
  // fires only on the strictly narrower, server-proven condition.
  onServerConfirmedDelivery?: () => void
): () => void {
  if (!uid)
    return function noopUnsubscribe() {
      /* no-op */
    };

  const docRef = doc(db, 'users', uid);

  // Guards the cold-cache suppression log against metadata-event spam — with
  // includeMetadataChanges below, the suppressed state can be delivered more
  // than once per listener; the signal only needs the first.
  let suppressionLogged = false;

  // GB #1526 meter state. `suppressedAt` is null until a cold-cache empty
  // snapshot is actually suppressed, so a healthy load emits nothing at all.
  let suppressedAt: number | null = null;
  let resolveTimer: ReturnType<typeof setTimeout> | null = null;
  // Latched as soon as this listener's outcome is DECIDED — including when the
  // volume cap suppresses the send, and including outcomes we deliberately do
  // not report. One listener can never contribute two counts.
  let outcomeCaptured = false;
  // Firestore's unsubscribe() stops FUTURE deliveries but does not cancel a
  // callback invocation already queued — AuthContext:236-243 documents the same
  // race. Without this flag, a snapshot landing after teardown would arm a
  // fresh timer that nothing can clear, reporting a phantom stuck user 10s
  // later (adversarial review, reproduced).
  let torndown = false;

  const clearResolveTimer = () => {
    if (resolveTimer !== null) {
      clearTimeout(resolveTimer);
      resolveTimer = null;
    }
  };

  /**
   * GB #1526: emit the meter as a real Sentry EVENT.
   *
   * Why this exists at all: the suppression previously reported via
   * `console.warn` (runs in the USER'S BROWSER — Vercel logs never see it) and
   * a Sentry breadcrumb (not an event; only attached as context to some LATER
   * event, so it is discarded whenever the suppression works). The meter
   * therefore read zero whether the condition never happened or happened
   * constantly, and #1435 cannot be closed without reading it.
   *
   * WARNING level, never `error` — the 911 alert rules key off error level, and
   * a working suppression must not page anyone (same reasoning as PR #1500's
   * downgrade). `uid` goes in `extra`, never `tags`: unbounded-cardinality tags
   * are a Sentry anti-pattern and `Sentry.setUser` already carries it.
   */
  const captureColdCacheOutcome = (
    kind: ColdCacheOutcome,
    elapsedMs: number,
    context: Record<string, unknown> = {}
  ) => {
    if (outcomeCaptured || torndown) return;
    // Latch on DECISION, before the volume cap can return early. Verified by
    // mutation to be defence in depth rather than a live invariant: every
    // terminal path also clears the pending timer, so moving this below the
    // cap breaks no test today. Kept because it is the cheap half of a
    // two-line defence, and the expensive half (the timer clear) is easy to
    // drop in a refactor.
    outcomeCaptured = true;
    if (coldCacheCaptureUsed[kind]) return;
    // Persistent throttle, checked after the in-memory cap: bounds a single
    // browser across hard page loads during a prolonged outage.
    try {
      if (!coldCacheThrottleAllows(kind, Date.now())) return;
    } catch {
      // Fail open — never let the throttle itself silence the meter.
    }
    try {
      // Distinct message per kind: the explicit fingerprint already splits the
      // two outcomes into separate Sentry issues, and the message is the issue
      // TITLE — identical titles would leave two indistinguishable rows in the
      // Sentry list, which is the readability problem this issue exists to fix.
      // It also gives the client flood guard (which keys message events by
      // message text) a distinct bucket per kind.
      Sentry.captureMessage(`[auth-lifecycle] ${kind}`, {
        level: 'warning',
        fingerprint: [COLD_CACHE_FINGERPRINT, kind],
        tags: { area: 'auth', kind },
        extra: { uid, elapsed_ms: elapsedMs, ...context },
      });
      // Marked spent only once the event has been ACCEPTED for sending.
      // Setting it before the call would let a throwing transport burn the
      // page's whole allowance while delivering nothing. Note the limit:
      // captureMessage is fire-and-forget, so a send that fails later on the
      // network still consumes the cap — this guards against a throwing
      // transport, not an undelivered one.
      coldCacheCaptureUsed[kind] = true;
      coldCacheThrottleCommit(kind, Date.now());
    } catch {
      // Instrumentation must never break the auth listener.
    }
  };

  /**
   * Same, but measuring elapsed time from `startedAt`. The subtraction lives
   * INSIDE the guard: as an argument expression it sat outside every try/catch,
   * so a hostile or broken `Date.now` threw straight into Firestore's snapshot
   * dispatch (adversarial review reproduced this).
   */
  const captureOutcomeSince = (
    kind: ColdCacheOutcome,
    startedAt: number,
    context: Record<string, unknown> = {}
  ) => {
    try {
      captureColdCacheOutcome(kind, Date.now() - startedAt, context);
    } catch {
      // Never reaches the caller.
    }
  };

  const unsubscribe = onSnapshot(
    docRef,
    // GB #1435 (Codex P2): with the cold-cache empty snapshot suppressed
    // below, a genuinely-absent doc's server confirmation arrives as a
    // METADATA-ONLY change (fromCache flips to false on the same nonexistent
    // doc). Firestore does not deliver metadata-only events without this
    // option — omitting it would strand ghost accounts (#971) on the
    // suppressed cache event, never firing the /signup recovery.
    { includeMetadataChanges: true },
    (snapshot) => {
      if (snapshot.exists()) {
        // A doc arrived, so this user is not stuck whatever its source.
        clearResolveTimer();
        const wasSuppressedAt = suppressedAt;
        const fromCache = snapshot.metadata.fromCache;
        // `hasPendingWrites` means THIS client has an unacknowledged write to
        // this document. It is the precise signal for "latency compensation
        // echoing our own write"; `fromCache` is broader and also covers a
        // server doc that merely has a local write outstanding.
        const hasPendingWrites = !!snapshot.metadata.hasPendingWrites;
        // The USER'S DOCUMENT IS DELIVERED FIRST. Every line of telemetry runs
        // strictly after this call and inside try/catch, so no instrumentation
        // failure — not even a hostile `Date.now` — can stall the auth flow
        // (adversarial review demonstrated the earlier ordering could).
        callback(snapshot.data() as UserDocument);
        // GB #1534: only a SERVER-sourced snapshot proves the watch stream
        // is actually connected — see this param's doc comment above.
        if (!fromCache) {
          try {
            onServerConfirmedDelivery?.();
          } catch {
            // Instrumentation must never surface to the user.
          }
        }
        if (wasSuppressedAt !== null) {
          // GB #1526 (both round-2 reviews): RECORD, never silently drop.
          //
          // This used to latch and report nothing when the doc arrived from
          // cache, on the theory that it could only be this client's own
          // signup write echoing back. The two round-2 reviewers reached
          // OPPOSITE conclusions about whether a plain login can also produce
          // that shape (an unacknowledged `updateLastLogin` patch riding along
          // with the real server doc), and neither could settle it without a
          // live backend. Dropping on a contested signal is the dangerous
          // choice: it loses genuine saves precisely in the login path #1435 is
          // about, and it does so silently and permanently for that listener.
          //
          // So the event is always emitted and the ambiguous conditions are
          // attached as fields. Filtering happens in Sentry, where it is
          // visible and reversible, instead of in code where it is invisible.
          // The ordinary-signup false positive that motivated the old drop is
          // already prevented upstream by the server-confirmed-absent latch.
          captureOutcomeSince('cold_cache_suppressed_recovered', wasSuppressedAt, {
            from_cache: fromCache,
            has_pending_writes: hasPendingWrites,
          });
        }
      } else if (snapshot.metadata.fromCache) {
        // GB #1435 (D1): on a cold cache Firestore delivers the FIRST snapshot
        // from the local cache before the server connection lands — for a user
        // whose doc EXISTS on the server that snapshot is exists()===false,
        // fromCache===true. Routing a user on it is the double-login bug.
        // Suppress: fire NO callback and wait for the server snapshot that
        // follows once the connection is up. If the client is offline no
        // callback ever fires — deliberate: never route a user off an empty
        // cache.
        if (!suppressionLogged) {
          // Set BEFORE the guarded block so a throw inside it can never cause
          // the suppression signal to be logged twice.
          suppressionLogged = true;
          // Everything below is instrumentation. It is wrapped so the claim
          // "this listener's behavior is unaffected" is structurally true, not
          // merely true in practice: `Date.now`, `setTimeout`, `console`, and
          // Sentry are all replaceable globals, and a throw here would land in
          // Firestore's snapshot dispatch.
          try {
            console.warn(
              '[auth-lifecycle] cold-cache-empty-suppressed',
              JSON.stringify({ uid, fromCache: true })
            );
            Sentry.addBreadcrumb({
              category: 'auth-lifecycle',
              message: 'cold-cache-empty-suppressed',
              level: 'info',
              data: { uid, fromCache: true },
            });
            // GB #1526: nothing is reported yet — a suppression that resolves in
            // 200ms is invisible to the user and needs no event. Start the clock
            // and report only the OUTCOME: a doc that arrives (a saved user), or
            // a suppression that never resolves (a user stuck on the spinner,
            // since resolveAuthLandingAction waits while the doc is loading).
            //
            // ⚠ READING THE NUMBER: this clock starts when Firestore RAISES the
            // empty cache snapshot, which it only does once its own
            // OnlineStateTracker has already given up — after ONLINE_STATE_TIMEOUT
            // (10s) or a watch-stream failure. The user was already watching a
            // spinner before t=0 here, so `elapsed_ms` UNDERSTATES the real wait
            // by roughly that much (zero-context review). Treat it as a lower
            // bound on user-visible delay, never as the total.
            suppressedAt = Date.now();
            // Never arm a timer for a listener that is already torn down: it
            // could not be cleared and would report a phantom stuck user.
            if (!torndown) {
              resolveTimer = setTimeout(() => {
                resolveTimer = null;
                // The whole body is guarded: an uncaught throw inside a timer
                // reaches window.onerror and Sentry would capture it at ERROR
                // level — the one thing this instrument must never do.
                try {
                  if (torndown || suppressedAt === null) return;
                  const elapsedMs = Date.now() - suppressedAt;
                  // GB #1526 (both round-2 reviews): RECORD, never silently
                  // drop. These two conditions used to `return`, which created
                  // the worst possible bias — `recovered` has no such filters,
                  // so the meter counted SAVES in exactly the conditions where
                  // it refused to count STUCK USERS. The ratio between the two
                  // is what #1435's close decision rests on, so an asymmetric
                  // filter is worse than no filter at all. Both sides are now
                  // recorded with the condition attached as a field, and the
                  // filtering happens in Sentry where it is visible.
                  //
                  // timer_late: fired well past its delay, so the tab was
                  //   throttled or frozen and the user was not staring at a
                  //   spinner for that whole span.
                  // hidden: the tab is not visible at this moment.
                  const timerLate =
                    elapsedMs >
                    COLD_CACHE_RESOLVE_TIMEOUT_MS +
                      COLD_CACHE_LATE_TIMER_TOLERANCE_MS;
                  const hidden =
                    typeof document !== 'undefined' &&
                    document.visibilityState === 'hidden';
                  console.warn(
                    '[auth-lifecycle] cold-cache-empty-unresolved',
                    JSON.stringify({ uid, elapsedMs, timerLate, hidden })
                  );
                  // Real measured elapsed, never the nominal timeout constant.
                  captureColdCacheOutcome(
                    'cold_cache_suppressed_unresolved',
                    elapsedMs,
                    { timer_late: timerLate, hidden }
                  );
              } catch {
                // Instrumentation must never surface to the user.
              }
            }, COLD_CACHE_RESOLVE_TIMEOUT_MS);
          }
          } catch {
            // Instrumentation must never surface to the user.
          }
        }
      } else {
        // Server-confirmed absence: a genuine ghost account (GB #971). Deliver
        // null so AuthContext keeps its existing signup-redirect flow.
        // Console + breadcrumb only — AuthContext's no-doc-signup-redirect is
        // the single Sentry capture for this condition (one event per
        // occurrence, per adversarial review; Sentry 429 risk is real, GB I5).
        // GB #1526: the server ANSWERED, so this user is not stuck — stop the
        // unresolved clock. Deliberately NOT counted as a save: the doc is
        // genuinely absent, so the D1 suppression only DELAYED a correct
        // /signup routing rather than preventing a wrong one. Counting it
        // would inflate the saved-user number with ghost accounts. The single
        // Sentry capture for this condition stays in AuthContext's
        // no-doc-signup-redirect (one event per occurrence).
        //
        // `outcomeCaptured` is latched here, not just the timer cleared. The
        // signup flow keeps this listener mounted: server says "no doc" →
        // AuthContext routes to /signup → the user submits →
        // createUserDocument writes → this same listener delivers exists().
        // Without the latch, `suppressedAt` is still set and that ordinary
        // signup would mint a FALSE saved-user event and burn the page's cap,
        // silencing a real save later (QA review, reproduced).
        clearResolveTimer();
        outcomeCaptured = true;
        console.warn(
          '[auth-lifecycle] user-doc-missing-server-confirmed',
          JSON.stringify({ uid, fromCache: false })
        );
        Sentry.addBreadcrumb({
          category: 'auth-lifecycle',
          message: 'user-doc-missing-server-confirmed',
          level: 'warning',
          data: { uid },
        });
        callback(null);
        // GB #1534: this branch is by definition server-confirmed
        // (fromCache is always false here — see the console.warn above).
        try {
          onServerConfirmedDelivery?.();
        } catch {
          // Instrumentation must never surface to the user.
        }
      }
    },
    // Listener runs in AuthContext for every authenticated user. Without an
    // onError handler, deadline-exceeded / unavailable on flaky mobile
    // networks bubbled up to the React tree and surfaced as a top-level
    // _app error. Keep the existing callback state and just log — a transport
    // error must never route a user.
    (err) => {
      console.warn(
        '[auth-lifecycle] snapshot-error',
        JSON.stringify({ uid, code: (err as { code?: string })?.code })
      );
      // GB #1526: Firestore terminates a listener when its error callback
      // fires, so no snapshot will ever follow. Stop the clock and report the
      // real cause — leaving the timer armed would file a transport failure
      // under "the cold cache never resolved", which is a different defect
      // with a different fix (QA review).
      clearResolveTimer();
      if (suppressedAt !== null) {
        captureOutcomeSince(
          'cold_cache_suppressed_transport_error',
          suppressedAt
        );
      }
      // GB #1534: never fire into an already-torn-down subscription — the
      // caller's own unsubscribe already ran its cleanup.
      if (!torndown) {
        try {
          onTransportError?.(err);
        } catch {
          // A broken retry hookup must never surface here.
        }
      }
    }
  );

  // GB #1526: the unresolved-suppression timer must die with the listener.
  // Without this, a user who navigates away (soft nav → AuthContext tears the
  // listener down) would still be reported as stuck 10s later, and the pending
  // timer would keep this closure alive.
  return function unsubscribeUserDocument() {
    torndown = true;
    clearResolveTimer();
    unsubscribe();
  };
}

/**
 * GB #1471: forces a server round-trip for the user document instead of
 * relying on the AuthContext onSnapshot listener (which can stall — e.g. a
 * cart-write/backgrounded-tab hiccup — leaving the UI showing stale
 * permissions/settings until a hard refresh). Used to confirm a write (like
 * setVacationMode) actually landed before showing success, independent of
 * whether the push stream is currently healthy. Errors propagate to the
 * caller so callers can decide how to degrade.
 */
export async function fetchUserDocumentFromServer(
  uid: string
): Promise<UserDocument | null> {
  const docSnap = await getDocFromServer(doc(db, 'users', uid));
  return docSnap.exists() ? (docSnap.data() as UserDocument) : null;
}

export async function getUserByAccountId(accountId: string) {
  const q = query(userRef, where('accountId', '==', accountId), limit(1));
  const docSnap = await getDocs(q);
  const user = docSnap.size ? docSnap.docs[0].data() : null;
  return user;
}

export async function getUserById(uid: string) {
  const user = await getDoc(getRef(uid));
  return user.data();
}

export async function getUsersByIds(uids: string[]): Promise<UserDocument[]> {
  const q = query(userRef, where('uid', 'in', uids.slice(0, 10)));
  const snap = await getDocs(q);
  return snap.docs.map((doc) => doc.data());
}

export async function getUserByEmail(email: string) {
  const q = query(userRef, where('email', '==', email), limit(1));
  const docSnap = await getDocs(q);
  const user = docSnap.size ? docSnap.docs[0].data() : null;
  return user;
}

export async function updateUserByKey(
  uid: string,
  key: keyof UserDocument,
  value: unknown
): Promise<void> {
  const updates = { [key]: value } as Partial<UserDocument>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  await updateDoc(getRef(uid), updates as any);
}

/**
 * Update user's last_login and last_active timestamps on successful login.
 * Called from AuthContext after email/password, social, or passwordless login.
 * NOT called for admin impersonation (signInWithToken) — preserves real user's
 * login time and activity tracking. See AuthContext.tsx isImpersonatingRef.
 */
export function updateLastLogin(uid: string): Promise<void> {
  return updateDoc(getRef(uid), {
    last_login: Date.now(),
    last_active: Date.now(),
  });
}

/**
 * Sync Firebase Auth emailVerified status to Firestore is_email_verified
 * Called from AuthContext when user logs in with emailVerified=true but Firestore shows false
 * This handles OAuth users (auto-verified) and users who verified via email link while logged out
 */
export function syncEmailVerified(uid: string): Promise<void> {
  return updateDoc(getRef(uid), { 'permissions.is_email_verified': true });
}

/**
 * Set one or more `permissions` flags on a user doc as INDEPENDENT
 * dot-path field writes — GB web#1469.
 *
 * `updateDoc(getRef(uid), { permissions: {...} })` replaces the WHOLE
 * `permissions` map, not just the keys passed in (Firestore's `updateDoc`
 * replaces a nested object field wholesale, it does not merge it), and
 * `updateUserDoc`'s schema parse strips/defaults keys the caller didn't
 * supply. A read-modify-write of the map (read the current permissions,
 * spread them, overwrite one key, write the whole map back) can therefore
 * wipe or stomp other admins' concurrent permission writes to the same
 * document — two admins clicking two different buttons in the same window
 * can silently undo each other. Writing `'permissions.<key>': value` touches
 * only the named flag, so it can never clobber a sibling flag or race a
 * concurrent single-flag writer.
 */
export function setUserPermissionFlags(
  uid: string,
  // `integrations` is excluded on purpose: it is a nested map (enabled /
  // ebay / lightspeed-retail), and `'permissions.integrations': {...}` would
  // REPLACE that whole submap. Only scalar flags may be written here; the
  // runtime check below enforces the same rule for untyped callers.
  patch: Omit<Partial<UserDocument['permissions']>, 'integrations'>
): Promise<void> {
  const dotted: Record<string, unknown> = {};
  for (const key of Object.keys(patch) as Array<keyof typeof patch>) {
    if (String(key).includes('.')) {
      throw new Error(
        `setUserPermissionFlags: invalid key "${String(key)}" — keys may not contain '.'`
      );
    }
    const value = patch[key];
    if (value === undefined) continue;
    if (value !== null && typeof value === 'object') {
      throw new Error(
        `setUserPermissionFlags: "${String(key)}" is an object — only scalar flags may be written (a map would replace the whole submap)`
      );
    }
    dotted[`permissions.${String(key)}`] = value;
  }

  if (Object.keys(dotted).length === 0) {
    throw new Error('setUserPermissionFlags: empty patch');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return updateDoc(getRef(uid), dotted as any);
}

export async function removeUserKey(uid: string, key: keyof UserDocument) {
  return await updateDoc(getRef(uid), {
    [key]: deleteField(),
  });
}

export function addUserAddress(uid: string, address: AddressDocument) {
  return updateDoc(getRef(uid), { addresses: arrayUnion(address) });
}

export async function removeUserAddress(uid: string, address: AddressDocument) {
  // check if theres only one address
  const user = await getUserById(uid);
  if (!user) return;
  if (!user.addresses || user.addresses.length === 1) return;

  return updateDoc(getRef(uid), { addresses: arrayRemove(address) });
}

/**
 * Follow a seller.
 *
 * The subscription lives entirely on the follower's own user doc: `following`
 * is an array of seller uids. gearfocus-api fans out the "New Items from
 * <seller>" notification by querying `where('following', 'array-contains',
 * seller_id)` when a listing is created (onCreateSideEffects), so writing this
 * array is the whole opt-in — there is no separate followers record to keep in
 * sync. arrayUnion makes a repeat follow a no-op rather than a duplicate.
 */
export function followSeller(uid: string, sellerId: string): Promise<void> {
  return updateDoc(getRef(uid), { following: arrayUnion(sellerId) });
}

export function unfollowSeller(uid: string, sellerId: string): Promise<void> {
  return updateDoc(getRef(uid), { following: arrayRemove(sellerId) });
}

/**
 * GB #691: remember that this logged-in person has been shown the one-time
 * "follow this seller" popup, so it never shows again on any of their devices.
 * The users update rule (firestore.rules) is a blocklist of privilege fields;
 * this field is not on it, so the owner may write it — the same as `following`.
 * Callers wrap this in a best-effort try/catch: a failure only means the popup
 * could reappear on another device, never a broken page.
 */
export function markSellerFollowPopupSeen(uid: string): Promise<void> {
  return updateDoc(getRef(uid), { seller_follow_popup_seen: true });
}

export function deleteUserDocument(uid: string): Promise<void> {
  return deleteDoc(getRef(uid));
}

// Keep existing public-profile imports working through the users barrel.
export {
  getUserByUsername,
  getUserByUsernameSlug,
} from './user.public.service';

// GB #2075: a log-safe summary of an attribution object: which fields are
// present, each string's length, and which look like they hold an email or an
// ad click id. Never the values themselves.
const SUSPECT_ATTRIBUTION_VALUE = /@|gclid|fbclid|msclkid|gbraid|wbraid/i;
function describeAttributionForLog(
  attribution: NonNullable<CreateUserDocumentArgs['attribution']>
) {
  const fields: string[] = [];
  const lengths: Record<string, number> = {};
  const suspect: string[] = [];
  for (const [key, value] of Object.entries(attribution)) {
    if (value === undefined) continue;
    fields.push(key);
    if (typeof value === 'string') {
      lengths[key] = Array.from(value).length;
      if (SUSPECT_ATTRIBUTION_VALUE.test(value)) suspect.push(key);
    }
  }
  return { fields, lengths, suspect };
}

// GB #2075: error TEXT never reaches a log (Codex re-review, PR #2093 HIGH):
// a message can echo request data. A bounded code and the message length only.
const SAFE_LOG_CODE = /^[a-z0-9_./-]{1,64}$/i;
function describeErrorForLog(err: unknown) {
  const code = (err as { code?: unknown } | null)?.code;
  const message = (err as { message?: unknown } | null)?.message;
  const safeCode =
    typeof code === 'string' && SAFE_LOG_CODE.test(code) ? code : 'unknown';
  const length = typeof message === 'string' ? message.length : 0;
  return `code=${safeCode} messageLength=${length}`;
}

// One retry after a short pause, for a transient failure (offline, timeout,
// an Auth outage on the server). Codex re-review, PR #2093 MEDIUM.
const STAMP_RETRY_DELAY_MS = 1_000;

// GB #2075: the attribution rule makes the field create-only for the client,
// so when the retry in createUserDocument drops it, the server stamps it
// instead (gearfocus-api users-recordSignupAttribution, Admin SDK, only if
// the profile has none). Best effort: the profile is already written, so a
// failure here (function not deployed yet, offline, refused) is retried once,
// logged once, and never reaches the signup flow.
async function recordDroppedSignupAttribution(
  uid: string,
  attribution: NonNullable<CreateUserDocumentArgs['attribution']>
) {
  for (let attempt = 1; ; attempt++) {
    try {
      const { data } = await httpsCallable<
        { attribution: NonNullable<CreateUserDocumentArgs['attribution']> },
        { status: string }
      >(functions, 'users-recordSignupAttribution', { timeout: 10_000 })({
        attribution,
      });
      // "stamped" and "already-present" both leave attribution on the
      // profile. "no-profile" and "outside-window" are final refusals, so
      // they are logged, not retried.
      const status = data?.status;
      if (status !== 'stamped' && status !== 'already-present') {
        const safeStatus =
          typeof status === 'string' && SAFE_LOG_CODE.test(status)
            ? status
            : 'unknown';
        logError(
          new Error(
            `signup-attribution-not-stamped uid=${uid} status=${safeStatus}`
          ),
          undefined,
          undefined,
          { route: 'signup' }
        );
      }
      return;
    } catch (err) {
      if (attempt < 2) {
        await new Promise((resolve) =>
          setTimeout(resolve, STAMP_RETRY_DELAY_MS)
        );
        continue;
      }
      logError(
        new Error(
          `signup-attribution-record-failed uid=${uid} ${describeErrorForLog(err)}`
        ),
        undefined,
        undefined,
        { route: 'signup' }
      );
      return;
    }
  }
}

export async function createUserDocument(
  args: CreateUserDocumentArgs,
  method: AuthProvider | 'email'
) {
  try {
    try {
      await setDoc(doc(db, 'users', args.uid), args);
    } catch (err) {
      if (
        !(err instanceof FirebaseError) ||
        err.code !== 'permission-denied' ||
        !args.attribution
      ) {
        throw err;
      }
      // onUserCreate may have created the profile first. Attribution is
      // create-only in the rules, so retry once without that field.
      const withoutAttribution = { ...args };
      delete withoutAttribution.attribution;
      try {
        await setDoc(doc(db, 'users', args.uid), withoutAttribution);
      } catch {
        // Keep the first error for the existing log and caller handling.
        throw err;
      }
      // GB #2075: record WHICH fields were dropped, never their values.
      // The allowed fields are free text, so a campaign link can carry an
      // email, a name, or "gclid=..." inside utm_campaign or landing_page
      // (Codex review, PR #2093 HIGH), and this line goes to Sentry and
      // Cloud Error Reporting. The values are recovered by the server stamp
      // below, not from this log.
      logError(
        new Error(
          `signup-attribution-dropped uid=${args.uid} attribution=${JSON.stringify(describeAttributionForLog(args.attribution))}`
        ),
        undefined,
        undefined,
        { route: 'signup' }
      );
      // After the retry, never before: the retry is a full overwrite and
      // would erase a value the server had already stamped.
      await recordDroppedSignupAttribution(args.uid, args.attribution);
    }
  } catch (err) {
    const original = err instanceof Error ? err : new Error(String(err));
    // Log a uid-annotated COPY so GCP Error Reporting / Sentry can correlate the
    // failure to a specific account, without putting uid in Sentry tags
    // (unbounded-cardinality tags are a Sentry anti-pattern — see AuthContext
    // and reportDashboardError for the same convention).
    const forLog = new Error(
      `no-doc-client-write-failed uid=${args.uid} ${describeErrorForLog(err)}`
    );
    logError(forLog, undefined, undefined, { route: 'signup' });
    // Re-throw the ORIGINAL error (not the log copy) so every caller keeps
    // FirebaseError.code intact — createUserDocument has seven call sites
    // (social + email signup across the auth forms), four of them live
    // (EmailPasswordSignupForm, EmailPasswordLoginForn, PasswordlessAuthForm,
    // SignupForm's social path); the other three are in SignupForm's email
    // path and LoginForm.tsx, which has zero importers anywhere in src/ and
    // is not rendered by any route (web#1800 qa-review, verified by grep).
    // formatAuthError() switches on .code.
    throw original;
  }
  try {
    // Guard separately from the setDoc above: the user doc write already
    // succeeded by this point, so a completeRegistration failure must never
    // surface to the caller as "we couldn't finish setting up your account"
    // (web#1800 adversarial review — the account IS created; only this
    // best-effort follow-up call failed). Logged, not swallowed, so an
    // SDK-init failure here (the httpsCallable(...) construction itself
    // throwing) stays visible. Note: a genuine outage of the
    // users-completeUserRegistration callable is swallowed by
    // completeRegistration itself (analytics.service.ts) and never reaches
    // this catch — that's a separate, pre-existing gap, not fixed here.
    await completeRegistration(args.uid, args.email, method);
  } catch (err) {
    const forLog = new Error(
      `completeRegistration-failed-post-doc-write uid=${args.uid} ${describeErrorForLog(err)}`
    );
    logError(forLog, undefined, undefined, { route: 'signup' });
  }
}

export async function updateUserRole(args: UpdateUserRoleArgs) {
  if (!args.uid) return null;
  const userRef = doc(db, 'users', args.uid);
  await updateDoc(userRef, {
    roles: args.newRole,
  });
}

export async function updateUsername(uid: string, username: string) {
  // Server-side validation - prevents bypass via DevTools or direct API calls
  // Throws ZodError if username contains reserved terms or invalid characters
  usernameSchema.parse(username);

  const userRef = doc(db, 'users', uid);
  const username_slug = await getUniqueSlug(username, uid);
  return updateDoc(userRef, { username, username_slug });
}

export async function updateUserDoc(uid: string, args: UpdateUserDocArgs) {
  if (!uid) return null;
  const userRef = doc(db, 'users', uid);
  const updatedUser = updateUserDocArgsSchema.parse({
    ...args,
  });

  await updateDoc(userRef, updatedUser);
}

export async function validateAddresses(
  addresses: AddressDocument[]
): Promise<ShipEngineValidateResponse[]> {
  const res = await httpsCallable<
    AddressDocument[],
    ShipEngineValidateResponse[]
  >(
    functions,
    'validateAddress'
  )(addresses);
  return res.data;
}

type AddressPostalFields = Pick<
  AddressDocument,
  | 'address_line1'
  | 'address_line2'
  | 'city_locality'
  | 'state_province'
  | 'postal_code'
  | 'country_code'
>;

export interface AddressValidationResult {
  status: 'verified' | 'warning' | 'error' | 'unavailable';
  matched_address: Partial<AddressPostalFields> | null;
  messages: string[];
  is_po_box: boolean;
}

/**
 * Calls the authed `/api/user/validate-address` route (GB #1419), which
 * validates against ShipEngine server-side and fails open (status:
 * 'unavailable') on outage/timeout — address management must not depend on
 * ShipEngine uptime. Sole consumer: AddressCard's "Set as Main Address"
 * flow. The dashboard AddAddressModal save path does NOT call this — it
 * already validates via StripeAddressForm's `validateAddresses` Cloud
 * Function above, which hard-blocks unverified addresses before Save is
 * even enabled, so a second ShipEngine round trip there would be redundant.
 */
export async function validateAddress(
  address: AddressPostalFields
): Promise<AddressValidationResult> {
  const res = await fetch('/api/user/validate-address', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      address_line1: address.address_line1,
      address_line2: address.address_line2,
      city_locality: address.city_locality,
      state_province: address.state_province,
      postal_code: address.postal_code,
      // The route requires an exact 2-char ISO code, but legacy/imported
      // stored addresses can carry 3-letter forms ("USA"/"CAN") — truncation
      // is correct for both markets we operate in. Without this, a legacy
      // address 400s and can never be set as primary (the route deliberately
      // does NOT fail open on 400, since malformed stored data could
      // otherwise skip the PO-Box gate).
      country_code: (address.country_code || 'US')
        .trim()
        .slice(0, 2)
        .toUpperCase(),
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `validateAddress failed (${res.status})`);
  }
  return await res.json();
}

export function canChangeUserName(user: UserDocument) {
  // can only change a name once every 30 days
  if (user.username_changed) {
    const lastChange = new Date(user.username_changed);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return lastChange < thirtyDaysAgo;
  }
  return true;
}

export async function getUserToken(emailOrUsername: string, uid?: string) {
  if (!uid) {
    const q = query(userRef, where('email', '==', emailOrUsername), limit(1));
    const q2 = query(
      userRef,
      where('username', '==', emailOrUsername),
      limit(1)
    );
    const [docSnap, docSnap2] = await Promise.all([getDocs(q), getDocs(q2)]);
    const foundDoc = !docSnap.empty
      ? docSnap
      : !docSnap2.empty
      ? docSnap2
      : null;
    if (!foundDoc) return null;
    uid = foundDoc.docs[0].id;
  }
  const { data } = await httpsCallable<{ uid: string }, { token: string }>(
    functions,
    'impersonateUser'
  )({ uid });
  return data.token;
}

export async function updateAuthUser(
  uid: string,
  emailVerified?: boolean,
  disabled?: boolean,
  email?: string
) {
  return httpsCallable<
    {
      uid: string;
      emailVerified?: boolean;
      disabled?: boolean;
      email?: string;
    },
    null
  >(
    functions,
    'updateAuthUser'
  )({
    uid,
    ...(emailVerified !== undefined && { emailVerified }),
    ...(disabled !== undefined && { disabled }),
    ...(email && { email }),
  });
}

export function generateUsername(name: string | null): string {
  // generate a username (last name + first initial of first name)
  if (!name) return 'user' + Math.floor(Math.random() * 1000000);
  const [firstName, lastName] = name.split(' ');
  return slugify(
    `${lastName}${firstName[0]}-${Math.floor(Math.random() * 1000000)}}`
  );
}

export function getValidUserDocument(
  uid: string,
  email: string,
  form: {
    firstName: string;
    lastName: string;
  },
  attribution?: AttributionData
): UserDocument {
  // getAttribution() does `JSON.parse(cookie) as AttributionData` -- a raw
  // cast, not a validated shape. A cookie that is valid JSON but the wrong
  // shape (tampered, cross-subdomain, or a future drift between the
  // AttributionData interface and this schema) would otherwise reach
  // userDocumentSchema.parse() below and THROW -- and every one of the 7
  // signup call sites wraps only createUserDocument() in try/catch, not
  // getValidUserDocument(). On the email/password path the Firebase Auth
  // account is already created by the time this runs, so an uncaught throw
  // here orphans the account: Auth user exists, Firestore doc never
  // written, no error surfaced, invisible in Sentry -- the 2026-03-26
  // failure shape this PR exists to avoid. safeParse never throws; an
  // invalid shape is silently dropped rather than blocking signup.
  const safeAttribution = attribution
    ? userDocumentSchema.shape.attribution.safeParse(attribution)
    : null;
  // Fail SILENT for the signup (never block it), but not SILENT overall: a
  // dropped attribution is the one path where the whole campaign-tracking
  // instrument (GB web#1732) can stop ringing with nobody hearing it -- a
  // future drift between AttributionData and this schema would zero
  // attribution on every signup site-wide, and the first symptom would be
  // the recruit-tracking report reading empty with no diagnostic trail.
  // logError, not a thrown error -- signup must still complete.
  if (attribution && safeAttribution && !safeAttribution.success) {
    logError(safeAttribution.error, undefined, undefined, { route: 'signup' });
  }
  const docData = userDocumentSchema.parse({
    name: `${form.firstName} ${form.lastName}`,
    first: form.firstName,
    last: form.lastName,
    phone: '',
    country: '',
    state: '',
    ...(safeAttribution?.success &&
      safeAttribution.data && { attribution: safeAttribution.data }),
    account_activated: false,
    badges: ['New User'],
    badge_counts: [],
    brands: [],
    created: Date.now(),
    email,
    favorites: [],
    following: [],
    from_web: true,
    hide_status: false,
    last_active: Date.now(),
    opted_out: false,
    photo: `https://ui-avatars.com/api/?background=random&length=2&name=${form.firstName}&size=256`,
    rating: 0,
    reviews: 0,
    uid,
    username: `user-${uid.slice(0, 5)}`,
    username_slug: `user-${uid.slice(0, 5)}`,
    username_changed: 0,
    stripe: { requests: [] },
    permissions: {
      is_verified: false,
      is_banned: false,
      is_seller: false,
      is_fraudulent: false,
      is_email_verified: false,
      is_restricted: false,
      can_message: false,
      can_buy: false,
      can_sell: false,
    },
    notifications: {
      staff: {
        push: true,
        email: true,
        sms: true,
      },
      arrivals: {
        push: true,
        email: true,
        sms: true,
      },
      inbox: {
        push: true,
        email: true,
        sms: true,
      },
      offer: {
        push: true,
        email: true,
        sms: true,
      },
      order: {
        push: true,
        email: true,
        sms: true,
      },
      followed: {
        push: true,
        email: false,
        sms: true,
      },
      offer_seller: {
        push: true,
        email: true,
        sms: true,
      },
      order_seller: {
        push: true,
        email: true,
        sms: true,
      },
      all: {
        push: true,
        email: true,
        sms: true,
      },
    },
  });
  return docData;
}

export async function getUserAddresses(
  user: UserDocument,
  realUserUid?: string
): Promise<AddressDocument[]> {
  if (user.uid === process.env.NEXT_PUBLIC_SUPPORT_ID && realUserUid) {
    const realUser = await getUserDocument({ uid: realUserUid });
    return realUser?.addresses ?? [];
  }
  return user.addresses ?? [];
}

export async function getUniqueSlug(
  username: string,
  uid?: string
): Promise<string> {
  const q = query(
    userRef,
    where('username_slug', '==', slugify(username)),
    limit(1)
  );
  const docSnap = await getDocs(q);
  if (docSnap.size) {
    const str = uid ? uid.slice(0, 5) : Math.floor(Math.random() * 1000000);
    return slugify(`${username}-${str}`);
  }
  return slugify(username);
}

type NexusState = {
  country: string;
  country_code: string;
  region: string;
  region_code: string;
};

export async function getNexusStates(): Promise<NexusState[]> {
  const ref = collection(db, 'nexus_states');
  const q = query(ref);
  const docSnap = await getDocs(q);
  return docSnap.docs.map((d) => d.data()) as NexusState[];
}

export async function updateSpotlightInfo(
  uid: string,
  spotlightInfo: {
    youtube_url?: string;
    featured_quote?: string;
    tagline?: string;
    banner_image?: string;
    recommended_gear?: Array<{
      title: string;
      description?: string;
      link?: string;
      image?: string;
    }>;
    is_spotlight?: boolean;
  }
) {
  if (!uid) return null;
  const userRef = doc(db, 'users', uid);

  // Validate the spotlight info against our schema
  const validatedInfo = spotlightInfoSchema.parse({
    ...spotlightInfo,
    is_spotlight: spotlightInfo.is_spotlight ?? true,
  });

  return updateDoc(userRef, {
    spotlight_info: validatedInfo,
  });
}

interface AccountLinkResponse {
  url: string;
  [key: string]: unknown;
}

export async function generateAccountLink(
  field: string,
  type: string
): Promise<AccountLinkResponse> {
  const res = await httpsCallable<
    { field: string; type: string },
    AccountLinkResponse
  >(
    functions,
    'stripe-generateAccountLink'
  )({ field, type });
  return res.data;
}

export async function validateSellerAccount(): Promise<
  Record<string, unknown>
> {
  const res = await httpsCallable<
    Record<string, never>,
    Record<string, unknown>
  >(functions, 'stripe-validateSellerAccount')();
  return res.data;
}
